import React from 'react';
import {
  WithStyles,
  withStyles,
  createStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import SurveyItem from '../../util/SurveyItem';
import {
  withSurveyContext,
  withIsReadOnly,
  WithIsReadOnlyInjectedProps,
  WithSurveyContextInjectedProps,
} from '../Contexts';
import SurveyField from '../SurveyField';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import withValidation, {
  WithValidationInjectedProps,
} from '../hoc/withValidation';
import ChoiceOptions from '../../util/ChoiceOptions';
import { FieldProps } from 'formik';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import { Radio, RadioGroup } from '@lifeomic/chroma-react/components/Radio';
import { Text } from '@lifeomic/chroma-react/components/Text';
import FilledByIndicator from '../FilledByIndicator';

export const testIds = {
  radioOption: (linkId: string, value: string) =>
    getSurveyItemTestId(linkId, value),
  errorMessage: (linkId: string) =>
    getSurveyItemTestId(linkId, 'table-group-mobile-variant-error'),
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(4),
      width: '100%', // TODO: this is a placeholder, should be responsive
    },
    radioGroupContainer: {
      marginTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    errorText: {
      color: theme.palette.error.main,
    },
    clearButton: {
      marginTop: theme.spacing(2),
    },
  });

interface FieldComponentOwnProps {
  surveyItem: SurveyItem;
  shouldShowError: boolean;
  error: string;
  choiceOptions: ChoiceOptions;
  isReadOnly: boolean;
}

type FieldComponentProps = FieldComponentOwnProps &
  FieldProps<any> &
  WithStyles<typeof styles>;

class FieldComponent extends React.PureComponent<FieldComponentProps> {
  // The field value may be represented as a complex object in form state, but must be represented as a simple value for the control
  wrapValue = (value: any) => {
    return this.props.choiceOptions.formToResponseValue(value);
  };

  unwrapValue = (responseValue: any) => {
    return this.props.choiceOptions.responseToFormValue(responseValue);
  };

  handleChange = (event: any) => {
    const { form, field } = this.props;

    const targetValue = event?.target?.value;
    if (!isNil(targetValue)) {
      const unwrappedCurrentValue = this.unwrapValue(this.props.field.value);
      if (isEqual(targetValue, unwrappedCurrentValue)) {
        // toggle off
        this.clearValue();
      } else {
        // toggle on
        const wrappedValue = this.wrapValue(targetValue);
        form.setFieldValue(field.name, wrappedValue);
      }
    }
  };

  handleClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const clickTargetValue = (event?.target as any)?.value;

    // only handle toggling off
    const selectedValue = this.unwrapValue(this.props.field?.value);
    if (!!clickTargetValue && isEqual(selectedValue, selectedValue)) {
      this.clearValue();
    }
  };

  clearValue = () => {
    const { form, field } = this.props;

    form.setFieldValue(field.name, null);
  };

  render() {
    const {
      field: { value, name },
      surveyItem,
      classes,
      shouldShowError,
      error,
      choiceOptions,
      isReadOnly,
    } = this.props;

    const selectedValue = this.unwrapValue(value);
    const radioGroupValue = isNil(selectedValue) ? '' : selectedValue;

    return (
      <div className={classes.root}>
        <FilledByIndicator surveyItem={surveyItem} />
        <RadioGroup
          key={`${name}-${selectedValue}`} // TODO: remove this when radio group is completely controlled
          title={surveyItem.label}
          value={radioGroupValue}
          onChange={this.handleChange}
          name={name}
        >
          {choiceOptions.options.map((option, index) => (
            <Radio
              {...getTestProps(
                testIds.radioOption(name, this.unwrapValue(option.value)),
              )}
              onClick={this.handleClick}
              key={index}
              value={this.unwrapValue(option.value)}
              label={option.display}
              disabled={isReadOnly}
            />
          ))}
        </RadioGroup>
        {shouldShowError && (
          <Text
            {...getTestProps(testIds.errorMessage(name))}
            size="caption"
            className={classes.errorText}
          >
            {error}
          </Text>
        )}
      </div>
    );
  }
}

interface TableGroupMobileVariantOwnProps {
  choiceOptions: ChoiceOptions;
  surveyItem: SurveyItem;
}

export type TableGroupMobileVariantProps = TableGroupMobileVariantOwnProps &
  WithStyles<typeof styles> &
  WithCheckForEnablementInjectedProps &
  WithIsReadOnlyInjectedProps &
  WithSurveyContextInjectedProps &
  WithValidationInjectedProps;

class TableGroupMobileVariant extends React.PureComponent<TableGroupMobileVariantProps> {
  render() {
    const {
      surveyItem,
      isEnabled,
      shouldShowError,
      error,
      validate,
      choiceOptions,
      classes,
      isReadOnly,
    } = this.props;

    if (!isEnabled) return null;

    return (
      <SurveyField
        validate={validate}
        responseAnswerType={surveyItem.responseValueType}
        name={surveyItem.link}
        component={FieldComponent}
        surveyItem={surveyItem}
        isEnabled={isEnabled}
        isReadOnly={isReadOnly}
        error={error}
        shouldShowError={shouldShowError}
        choiceOptions={choiceOptions}
        classes={classes}
      />
    );
  }
}

export default withStyles(styles)(
  withIsReadOnly(
    withCheckForEnablement(
      withValidation(withSurveyContext(TableGroupMobileVariant)),
    ),
  ),
);
